import { useCallback } from 'react';
import { useAppDispatch } from "../../../../../../../../../app/hooks";
import {
    ConditionOperator,
    ConditionField,
    FieldOptionDTO
} from "../../../../../../../../../api/generated";
import {
    updateCondition,
    deleteCondition,
    UpdateConditionRequestUI
} from "../../../../toolSettingsSlice";

export const useConditionHandlers = (
    condition: UpdateConditionRequestUI,
    sectionIndex: number,
    groupIndex: number,
    conditionIndex: number,
    fieldOptions: FieldOptionDTO[]
) => {
    const dispatch = useAppDispatch();

    const handleFieldChange = useCallback((field: ConditionField) => {
        const newFieldOption = fieldOptions.find(opt => opt.field === field);
        const firstOperator = newFieldOption?.availableOperators?.[0];

        dispatch(updateCondition({
            sectionIndex,
            groupIndex,
            conditionIndex,
            condition: {
                ...condition,
                conditionField: field,
                operator: (firstOperator?.value as ConditionOperator) || ConditionOperator.Equals,
                comparedValues: undefined
            }
        }));
    }, [dispatch, condition, sectionIndex, groupIndex, conditionIndex, fieldOptions]);

    const handleOperatorChange = useCallback((operatorValue: string) => {
        const newFieldOption = fieldOptions.find(opt => opt.field === condition.conditionField);
        const newOperator = newFieldOption?.availableOperators?.find(
            op => op.value === operatorValue
        );

        let newComparedValues: string[] | undefined = undefined;
        
        if (newOperator?.requireInputField) {
            if (newOperator.requiredMultiValue) {
                newComparedValues = condition.comparedValues;
            } else {
                newComparedValues = condition.comparedValues && condition.comparedValues.length > 0 
                    ? [condition.comparedValues[0]] 
                    : undefined;
            }
        }

        dispatch(updateCondition({
            sectionIndex,
            groupIndex,
            conditionIndex,
            condition: {
                ...condition,
                operator: operatorValue as ConditionOperator,
                comparedValues: newComparedValues
            }
        }));
    }, [dispatch, condition, sectionIndex, groupIndex, conditionIndex, fieldOptions]);

    const handleValueChange = useCallback((value: string) => {
        dispatch(updateCondition({
            sectionIndex,
            groupIndex,
            conditionIndex,
            condition: {
                ...condition,
                comparedValues: value ? [value] : undefined
            }
        }));
    }, [dispatch, condition, sectionIndex, groupIndex, conditionIndex]);

    const handleMultiValueChange = useCallback((values: string[]) => {
        dispatch(updateCondition({
            sectionIndex,
            groupIndex,
            conditionIndex,
            condition: {
                ...condition,
                comparedValues: values.length > 0 ? values : undefined
            }
        }));
    }, [dispatch, condition, sectionIndex, groupIndex, conditionIndex]);

    const handleDelete = useCallback(() => {
        dispatch(deleteCondition({ sectionIndex, groupIndex, conditionIndex }));
    }, [dispatch, sectionIndex, groupIndex, conditionIndex]);

    return {
        handleFieldChange,
        handleOperatorChange,
        handleValueChange,
        handleMultiValueChange,
        handleDelete
    };
};

